import React, { useEffect, memo } from 'react';
import { Drawer, Spin, Alert } from '../../components';
import { useLazyGetCloudAccountByIdQuery } from '../../api';
import { ApplianceConfiguration } from './components/ApplianceConfiguration';
import { ApplianceReadiness } from './components/ApplianceReadiness';
import { useCanDownloadLogs } from './hooks/useCanDownloadLogs';
import { SourceVsphereConfiguration } from './components/SourceVsphereConfiguration';
import { isVmLevelMigrationEnabled, showSourceWorkers } from './utils';
import { SourceWorkers } from './components/SourceWorkers';
import { AZURE_TYPES, CLOUD_TYPE, VSPHERE_TYPES } from '../../constants';

const cloudTypesForDrawerContent = [
    CLOUD_TYPE.NC2,
    CLOUD_TYPE.HPE_VME,
    CLOUD_TYPE.HYPERV,
    CLOUD_TYPE.OPENSHIFT,
    CLOUD_TYPE.OPENSTACK,
    CLOUD_TYPE.VSPHERE,
    AZURE_TYPES.AZURE_DR,
    VSPHERE_TYPES.VSPHERE_DR,
    VSPHERE_TYPES.VSPHERE_OFFLINE_REPLICAS,
];

export const MigrationApplianceDetailsDrawer = memo(({ open, onClose, applianceId }) => {
    const [fetchSingleCloudAccount, { data: cloudAccountData, isLoading, isFetching, error }] =
        useLazyGetCloudAccountByIdQuery();
    const logsUrl = useCanDownloadLogs(cloudAccountData);

    useEffect(() => {
        if (open && applianceId) {
            fetchSingleCloudAccount(applianceId);
        }
    }, [applianceId, open, fetchSingleCloudAccount]);

    if (error) {
        return (
            <Drawer
                open={open}
                onClose={onClose}
                title="Migration Appliance Details"
                size={600}
                placement="right"
                mask={false}
            >
                <Alert
                    title="Error Loading Migration Appliance Details"
                    description={error.message}
                    type="error"
                    showIcon
                />
            </Drawer>
        );
    }
    return (
        <Drawer
            className="migration-appliance-details-drawer"
            open={open}
            onClose={onClose}
            title="Migration Appliance Details"
            size={700}
            placement="right"
        >
            {isLoading || isFetching ? (
                <div className="loading-screen">
                    <Spin size="large" />
                </div>
            ) : (
                cloudAccountData && (
                    <div className="drawer-content">
                        {cloudTypesForDrawerContent.includes(cloudAccountData.type) ? (
                            <ApplianceConfiguration
                                appliance={cloudAccountData.appliance}
                                error={error}
                                cloudAccountType={cloudAccountData.type}
                                cloudAccountName={cloudAccountData.name}
                                logsUrl={logsUrl}
                            />
                        ) : (
                            <ApplianceReadiness
                                appliance={cloudAccountData.appliance}
                                error={error}
                                cloudAccountId={cloudAccountData.id}
                                cloudAccountName={cloudAccountData.name}
                                cloudAccountType={cloudAccountData.type}
                                logsUrl={logsUrl}
                            />
                        )}

                        {isVmLevelMigrationEnabled(cloudAccountData) && (
                            <SourceVsphereConfiguration cloudAccountData={cloudAccountData} />
                        )}

                        {showSourceWorkers(cloudAccountData) && (
                            <SourceWorkers cloudAccountData={cloudAccountData} error={error} />
                        )}
                    </div>
                )
            )}
        </Drawer>
    );
});
